import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import Tooltip from 'primevue/tooltip'
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primeuix/themes'
import router from './router'
import App from './App.vue'
import { buildPrimaryPreset, DEFAULT_PRIMARY_COLOR } from '@/composables/useLayout'
import { i18n, initialPrimeVueLocale, registerPrimeVueConfig } from './i18n'
import { configureDateLocale, configureDateTimeZone } from '@/utils/date.utils'
import { useAuthStore } from '@/stores/auth.store'

import 'primeicons/primeicons.css'
import '@/assets/layout/layout.scss'

// Le date seguono la lingua scelta in app. Per 'en' si preferisce la variante regionale del
// browser quando è compatibile (en-GB -> 14/08/2026, en-US -> 8/14/2026): il formato giusto
// dipende dal paese, non dalla lingua.
configureDateLocale(() => {
  const appLocale = i18n.global.locale.value
  if (appLocale === 'it') return 'it-IT'
  const navigatorLocale = navigator.language
  if (navigatorLocale?.toLowerCase().startsWith(appLocale.toLowerCase())) return navigatorLocale
  return appLocale
})

const app = createApp(App)

app.use(createPinia())

// Gli ISTANTI si rendono nel fuso risolto dal server, non in quello del browser: due
// redattori che lavorano sulla stessa coda devono leggere lo stesso orario per lo stesso
// record. Il resolver è una funzione e non un valore per due motivi: qui sopra Pinia è
// appena stata installata (leggere lo store prima getterebbe "no active Pinia"), e la
// lettura dentro il render fa del fuso una dipendenza reattiva — cambiarlo riformatta le
// date senza ricaricare.
configureDateTimeZone(() => useAuthStore().timeZone ?? undefined)

app.use(router)
app.use(i18n)
// Aura di serie ha il primario emerald: il preset va esteso qui, all'installazione del
// plugin, perche' il colore deve valere anche sul login — che sta fuori da AppLayout, quindi
// fuori dalla portata del pannello Settings.
app.use(PrimeVue, {
  theme: {
    preset: definePreset(Aura, buildPrimaryPreset(DEFAULT_PRIMARY_COLOR)),
    options: {
      darkModeSelector: '.app-dark',
    },
  },
  locale: initialPrimeVueLocale,
})

// La config di PrimeVue va catturata qui, dove il plugin è appena stato installato: il cambio
// di lingua può partire anche da fuori il setup di un componente (dopo l'await di una fetch,
// in un handler asincrono), e lì `usePrimeVue()` — che è un inject — lancia "PrimeVue is not
// installed!".
registerPrimeVueConfig(app.config.globalProperties.$primevue.config)

document.documentElement.setAttribute('lang', i18n.global.locale.value)
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

app.mount('#app')
